/**
 * Global Client Hook
 *
 * Custom hook that provides access to the globally selected client.
 * This hook handles the logic for:
 * - Super admins: Can select any client from dropdown
 * - Regular users: Automatically uses their tenant's client
 * - Provides consistent client context across the application
 */

import { useEffect } from "react";
import { useAuthStore } from "../stores/auth-store";
import {
  useSelectedClient,
  useClientActions,
  useClientList,
} from "../stores/client-store";
import { useClientService } from "./use-client-service";

export interface GlobalClientReturn {
  /** Currently selected client */
  selectedClient: any | null;
  /** Whether client data is loading */
  loading: boolean;
  /** Any error with client selection */
  error: string | null;
  /** Whether the current user is a super admin (can select clients) */
  isSuperAdmin: boolean;
  /** Available clients (for super admins) */
  availableClients: any[];
  /** Function to change selected client (super admins only) */
  selectClient: (clientId: string) => void;
}

/**
 * Hook to get and manage the globally selected client
 */
export function useGlobalClient(): GlobalClientReturn {
  const { user } = useAuthStore();
  const {
    client: selectedClient,
    loading: selectedClientLoading,
    error: selectedClientError,
  } = useSelectedClient();
  const { setSelectedClient } = useClientActions();
  const {
    clients,
    loading: clientsLoading,
    error: clientsError,
  } = useClientList();
  const { loadClients } = useClientService();

  // Check if user is super admin
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  // Auto-load and select appropriate client for regular users
  useEffect(() => {
    if (!user) return;

    if (isSuperAdmin) {
      // Super admin: Load clients if not loaded
      if (clients.length === 0 && !clientsLoading) {
        console.log("🔄 Super admin loading clients...");
        loadClients(true);
        return;
      }

      // If clients are loaded, ensure selected client is valid
      if (clients.length > 0) {
        if (!selectedClient) {
          // No client selected - auto-select first client
          console.log("🔄 Super admin auto-selecting first client:", clients[0]);
          setSelectedClient(clients[0]);
        } else {
          // Validate that selected client still exists in the loaded clients
          const isValidClient = clients.some(
            (client) =>
              client.id === selectedClient.id ||
              client.clientId === selectedClient.id ||
              client.id === selectedClient.clientId
          );

          if (!isValidClient) {
            console.log("🔄 Super admin selected client is invalid, selecting first available:", clients[0]);
            setSelectedClient(clients[0]);
          } else {
            console.log("🔄 Super admin selected client is valid:", selectedClient);
          }
        }
      }
    } else {
      // Regular user: Load clients if not loaded, then auto-select their tenant's client
      if (user.tenantId) {
        if (clients.length === 0 && !clientsLoading) {
          loadClients(true);
        } else if (clients.length > 0) {
          const userClient = clients.find(
            (client) =>
              client.id === user.tenantId || client.clientId === user.tenantId
          );
          if (userClient && (!selectedClient || selectedClient.id !== userClient.id)) {
            setSelectedClient(userClient);
          }
        }
      }
    }
  }, [
    user,
    isSuperAdmin,
    selectedClient,
    clients,
    clientsLoading,
    loadClients,
    setSelectedClient,
  ]);

  // Function to select a client (for super admins)
  const selectClient = (clientId: string) => {
    if (!isSuperAdmin) {
      console.warn("Only super admins can select clients");
      return;
    }

    const client = clients.find((c) => c.id === clientId);
    if (client) {
      setSelectedClient(client);
    }
  };

  return {
    selectedClient,
    loading: selectedClientLoading || clientsLoading,
    error: selectedClientError || clientsError,
    isSuperAdmin,
    availableClients: clients,
    selectClient,
  };
}

/**
 * Hook to get just the selected client (most common use case)
 */
export function useCurrentClient() {
  const { selectedClient, loading, error } = useGlobalClient();
  return { client: selectedClient, loading, error };
}

/**
 * Hook to check if a client is currently selected
 */
export function useHasSelectedClient(): boolean {
  const { selectedClient } = useGlobalClient();
  return !!selectedClient;
}
